import { useState, useEffect } from 'react';
import axios from 'axios';
import { getClassApiUrl } from '../config/globalConfig';
import './StartClassModal.css';

export default function StartClassModal({ isOpen, onClose, onStartClass, teacherId }) {
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // 🔹 API URLs - Using global config
    const GET_CLASSES_URL = getClassApiUrl('GET_TEACHER_CLASSES_TODAY');
    const START_CLASS_URL = getClassApiUrl('START_CLASS');

    useEffect(() => {
        if (isOpen && teacherId) {
            fetchTodayClasses();
        }
    }, [isOpen, teacherId]);

    const fetchTodayClasses = () => {
        setIsLoading(true);
        setError('');

        const apiUrl = `${GET_CLASSES_URL}?teacher_id=${teacherId}`;

        axios
            .get(apiUrl)
            .then((response) => {
                if (response.data.result) {
                    setClasses(response.data.Classes);
                } else {
                    setError(response.data.publicMessage);
                }
            })
            .catch((err) => {
                setError('Failed to fetch classes');
                console.error('Error fetching classes:', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleStartClass = async (classId) => {
        try {
            onStartClass(classId);
            onClose();
        } catch (error) {
            alert('Failed to start class');
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="start-class-modal-overlay">
                <div className="start-class-modal-container">
                    {/* Header */}
                    <div className="start-class-modal-header">
                        <h2 className="start-class-modal-title">Start Today's Classes</h2>
                        <p className="start-class-modal-subtitle">Select a class to begin your live session</p>
                        <button 
                            onClick={onClose} 
                            className="start-class-modal-close"
                        >
                            ×
                        </button>
                    </div>

                    {/* Content */}
                    <div className="start-class-modal-content">
                        {isLoading ? (
                            <div className="start-class-loading">
                                <div className="start-class-spinner"></div>
                                <p className="start-class-loading-text">Loading classes...</p>
                            </div>
                        ) : error ? (
                            <div className="start-class-error">
                                <p className="start-class-error-text">{error}</p>
                                <button
                                    onClick={fetchTodayClasses}
                                    className="start-class-retry-btn"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : classes.length === 0 ? (
                            <div className="start-class-empty">
                                <div className="start-class-empty-icon">📚</div>
                                <p className="start-class-empty-text">No classes scheduled for today</p>
                            </div>
                        ) : (
                            <div className="start-class-list">
                                {classes.map((classItem) => (
                                    <div key={classItem.id} className="start-class-item">
                                        <div className="start-class-item-header">
                                            <div className="start-class-item-info">
                                                <div className="start-class-item-title">
                                                    <div className="start-class-item-status"></div>
                                                    <h3 className="start-class-item-name">
                                                        {classItem.batch_name}
                                                    </h3>
                                                </div>
                                                <div className="start-class-item-meta">
                                                    <div className="start-class-item-meta-item">
                                                        <span>📅</span>
                                                        <span>{new Date(classItem.class_date).toLocaleDateString()}</span>
                                                    </div>
                                                    <div className="start-class-item-meta-item">
                                                        <span>🕐</span>
                                                        <span>{classItem.class_time}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleStartClass(classItem.id)}
                                                className="start-class-start-btn"
                                            >
                                                Start Class
                                            </button>
                                        </div>

                                        {classItem.students?.length > 0 && (
                                            <div className="start-class-students">
                                                <div className="start-class-students-header">
                                                    <span>👥</span>
                                                    <span>Students ({classItem.students.length})</span>
                                                </div>
                                                <div className="start-class-students-list">
                                                    {classItem.students.map((student) => (
                                                        <div key={student.id} className="start-class-student">
                                                            <div className="start-class-student-avatar">
                                                                {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                                                            </div>
                                                            <div className="start-class-student-info">
                                                                <p className="start-class-student-name">
                                                                    {student.first_name} {student.last_name}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}